import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { Validator as ValidatorDefinition } from '@adornis/validation/validation.js';
import { Validator } from '@adornis/validation/validator.js';
import * as m from '../../../localization/out/messages.js';

/**
 * Turn an entity's `@validate` decorators into a validator for `useGeneratedForm`.
 *
 * ## Why this exists
 *
 * `useGeneratedForm` builds a *functional* form, and a functional form does **not**
 * read the decorators off its document — that is deliberate, since it also supports
 * plain objects. With no `validator` in the options, `form.validate()` short-circuits
 * to `null` on its very first line, so every required field silently passes and the
 * caller's `if (err) return` never fires.
 *
 * Most forms in this app get away with it because they end in `entity.save()`, which
 * re-runs the same validators before the write. Forms over throwaway client-only
 * entities (see `PDFUpload`) have no such safety net and validate nothing at all.
 *
 * ## Two things to watch out for
 *
 * 1. **Pass `fields` whenever the form is a partial view of a larger entity.** The
 *    full decorator set of `User`, for instance, marks `suspentions` as required —
 *    a form that only edits the name would demand fields it never renders and could
 *    never be submitted.
 * 2. **Decorators do not reach into nested entities or arrays.** The functional form
 *    walks *this object*, not the document, so `MWUserData.motivations` — whose rules
 *    live on `Motivation` — is invisible here and would be skipped without a peep.
 *    Hand-write a nested validator for those, e.g.
 *    `{ motivations: { motivation: minLength(60) } }` (see the motivation step).
 *
 * @param entity  the decorated entity class
 * @param fields  optional allow-list of fields the form actually renders
 */
export const entityValidator = <T extends AdornisEntity>(
  entity: abstract new (...args: any[]) => T,
  fields?: Array<Extract<keyof T, string>>,
): ValidatorDefinition => {
  const functions = Validator.fromDecoratedClass(entity).functions as ValidatorDefinition;
  if (!fields) return functions;
  return Object.fromEntries(
    fields.map(field => {
      const validationFunction = functions[field];
      // Loud on purpose: a typo'd or undecorated field name would otherwise silently
      // drop back to "no validation", which is the exact bug this helper exists to fix.
      if (!validationFunction)
        throw new Error(`entityValidator: "${String(entity)}" has no @validate rule for field "${field}"`);
      return [field, validationFunction];
    }),
  );
};

/**
 * `translateError` for `useGeneratedForm`, which hands us the bare translationKey of
 * the failed rule (or the raw message if the rule has none). Without it, a field's
 * inline error renders as the untranslated `Key consent is required!`.
 *
 * The generic `validation_non_optional` is special-cased: its template interpolates a
 * `{key}` field label that this callback never receives, so it would render as
 * `Wert "undefined" ist erforderlich!`. Inline, right underneath the field's own
 * label, a terse "Pflichtfeld" is the better message anyway — the error *banner*
 * still spells the field out in full via {@link formatFormError}.
 */
export const translateValidationError = (translationKey: string): string => {
  if (!translationKey) return '';
  if (translationKey === 'validation_non_optional') return m.validation_required_short();
  return (m as Record<string, any>)[translationKey]?.() ?? translationKey;
};
